import React, { useState } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { ArrowRight, LayoutDashboard, ClipboardList, UserCircle } from 'lucide-react';
import ReportIndexTab from './ReportIndexTab';
import InformationForm from './InformationForm';

/**
 * Dashboard Gateway — sequential checklist accessible from the Main Dashboard:
 *
 *   Step 1: Index 1 & 2  (personal daily attendance / checklist)
 *           → "Proceed to Information Form →"  (DM / ADM / CP only)
 *           → "Back to Main Dashboard →"       (all other roles)
 *
 *   Step 2: Information Form  (DM / ADM / CP only)
 *           → "Back to Main Dashboard →"
 *
 * Accessed via /dashboard/checklist (linked from the Main Dashboard banner).
 */
const DashboardGateway = () => {
  const { user } = useOutletContext();
  const navigate = useNavigate();

  const [step, setStep] = useState('index');

  const isCMD = (user?.role || '').toLowerCase() === 'cmd';

  const isHR =
    user?.department === 'hr' ||
    user?.role === 'super_admin' ||
    user?.department === 'it' ||
    isCMD;

  const isHierarchyRole = ['dm', 'adm', 'cp'].includes((user?.role || '').toLowerCase());

  const fullName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim();

  /* ── Shared banner ── */
  const Banner = ({ icon, title, subtitle, onProceed, btnLabel, btnTestId }) => (
    <div
      className="rounded-2xl mb-4 px-6 py-5 flex flex-wrap items-center gap-4 shadow-lg"
      style={{ background: 'linear-gradient(135deg,#0f172a 0%,#1e3a8a 50%,#1f3864 100%)' }}
    >
      <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center flex-shrink-0">
        {icon}
      </div>
      <div className="flex-1 min-w-[200px]">
        <h1 className="text-white font-extrabold text-xl tracking-tight" style={{ fontFamily: 'Manrope' }}>
          {title}
        </h1>
        <p className="text-blue-200 text-xs mt-1">{subtitle}</p>
      </div>
      <button
        onClick={onProceed}
        data-testid={btnTestId || 'gateway-proceed-btn'}
        className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold text-sm px-5 py-2.5 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 group"
      >
        <LayoutDashboard className="w-4 h-4" />
        <span>{btnLabel}</span>
        <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
      </button>
    </div>
  );

  /* ── Step 1: Index 1 & 2 ── */
  if (step === 'index') {
    const onIndexProceed = isHierarchyRole
      ? () => setStep('info')
      : () => navigate('/dashboard/main');

    return (
      <div className="p-4 lg:p-6" data-testid="dashboard-gateway-index">
        <Banner
          icon={<ClipboardList className="w-6 h-6 text-amber-300" />}
          title={`Index 1 & 2 — ${fullName}`}
          subtitle={isHierarchyRole
            ? 'Fill your daily attendance checklist. All entries auto-route to Sahil Karale (Asst HR).'
            : 'Fill your daily attendance checklist. All entries auto-route to Sahil Karale (Asst HR).'}
          onProceed={onIndexProceed}
          btnLabel={isHierarchyRole ? 'Proceed to Information Form' : 'Back to Main Dashboard'}
          btnTestId={isHierarchyRole ? 'proceed-to-info-form-btn' : 'back-to-main-btn'}
        />
        <ReportIndexTab user={user} isHR={isHR} startInAllView={isHR} />
      </div>
    );
  }

  /* ── Step 2: Information Form (DM / ADM / CP only) ── */
  return (
    <div className="p-4 lg:p-6" data-testid="dashboard-gateway-info">
      <Banner
        icon={<UserCircle className="w-6 h-6 text-amber-300" />}
        title={`Your Information Form — ${fullName}`}
        subtitle="Review and complete your profile information."
        onProceed={() => navigate('/dashboard/main')}
        btnLabel="Back to Main Dashboard"
        btnTestId="back-to-main-from-info-btn"
      />
      <InformationForm />
    </div>
  );
};

export default DashboardGateway;
